import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  CircularProgress,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
} from '@material-ui/core';
import { CSSProperties } from 'styled-components';
import Routes from './Routes';
import Header from './components/Header';
import LoginScreen from './components/LoginScreen';
import globalsStore, { GlobalsContext } from './store/Globals';
import Drawer from './components/Drawer';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';

const containerStyle = (headerHeight?: string | number): CSSProperties => ({
  display: 'flex',
  height: '100vh',
  justifyContent: 'center',
  alignItems: 'center',
  background: '#f5f5f5',
  ...(headerHeight && {
    flexDirection: 'column',
    height: `calc(100vh - ${headerHeight}`,
  }),
});

const App = observer(() => {
  const globals = useContext(GlobalsContext);
  const [loading, setLoading] = useState(true);
  const [resetSchema, setResetSchema] = useState(false);
  const [server, setServer] = useState<string>(globals.baseAddress);
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    setServer(globals.baseAddress);
  }, [globals.baseAddress])

  useEffect(() => {
    (async () => {
      let appName = 'Core Blockchain WebClient';
      
      try {
        appName = await globals.resetGlobals();
      } catch (err) {
        setResetSchema(true);
      }

      setLoading(false);
      document.title = appName;
    })();
  }, [globals, resetSchema]);

  const authToken = localStorage.getItem('@CoreBlockchain:authToken');

  const handleLogin = async (nodeAddress: string, clientId: string, clientSecret: string) => {
    setLoginLoading(true);
    try {
      // Definir o endereço do servidor primeiro
      localStorage.setItem('@CoreBlockchain:restServer', nodeAddress);
      globals.setBaseAddress(nodeAddress);
      
      // Definir o token de autenticação
      globals.setAuthToken(clientId, clientSecret);
      
      // Desabilitar a configuração do servidor
      globals.setConfigServer(false);
      
      // Recarregar a página para aplicar as novas configurações
      window.location.reload();
    } catch (error) {
      console.error('Erro durante o login:', error);
      setLoginLoading(false);
    }
  };

  if (globals.configServer) {
    if (authToken === "askForToken") {
      return (
        <LoginScreen
          initialNodeAddress={server || globals.baseAddress}
          onLogin={handleLogin}
          loading={loginLoading}
          isSecure={true}
        />
      );
    }

    return (
      <div style={containerStyle(globals.headerHeight)}>
        <Paper style={{ width: '350px', padding: '24px' }}>
          <Typography gutterBottom color="primary">
            Core node address
          </Typography>

          <Divider style={{ padding: '1px', marginBottom: '6px' }} />

          <Typography>Core Node: {globals.baseAddress}</Typography>

          <TextField
            fullWidth
            value={server}
            label="Core Node"
            placeholder="Change Core Node"
            onChange={(e) => {
              setServer(e.target.value);
            }}
          />

          <div style={{ marginTop: '24px' }}>
            <Button
              variant="outlined"
              style={{ marginRight: '15px' }}
              onClick={() => {
                localStorage.setItem('@CoreBlockchain:restServer', server);
                globals.setBaseAddress(server);
                globals.setConfigServer(false);
                window.location.reload();
              }}
            >
              Save
            </Button>

            <Button
              variant="outlined"
              style={{ marginRight: '15px' }}
              onClick={() => globals.setConfigServer(false)}
            >
              Cancel
            </Button>
          </div>
        </Paper>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={containerStyle()}>
        <Paper className="loadingPaper">
          <Typography gutterBottom color="primary">
            Searching for Core Blockchain info...
          </Typography>

          <Divider style={{ padding: '1px' }} />

          <Typography gutterBottom style={{ paddingTop: '6px' }}>
            Core Node: {globals.baseAddress}
          </Typography>

          <CircularProgress style={{ marginTop: '10px' }} />
        </Paper>
      </div>
    );
  }

  if (resetSchema) {
    if (authToken === "askForToken") {
      return (
        <LoginScreen
          initialNodeAddress={globals.baseAddress}
          onLogin={handleLogin}
          loading={loginLoading}
          isSecure={true}
        />
      );
    }

    return (
      <div style={containerStyle()}>
        <Paper style={{ padding: '24px', maxWidth: '400px' }}>
          <Typography variant="h6" gutterBottom color="error">
            Connection Error
          </Typography>
          
          <Typography gutterBottom>
            Could not connect to Core Blockchain Node
          </Typography>

          <Typography variant="body2" color="textSecondary" gutterBottom> 
            Core Node: {globals.baseAddress}
          </Typography>

          <Divider style={{ margin: '16px 0' }} />

          <div style={{ marginTop: '24px' }}>
            <Button
              variant="contained"
              color="primary"
              style={{ marginRight: '15px' }}
              onClick={() => globals.setConfigServer(true)}
            >
              CONFIGURE
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                setResetSchema(false);
                setLoading(true);
              }}
            >
              RETRY
            </Button>
          </div>
        </Paper>
      </div>
    );
  }

  return (
    <GlobalsContext.Provider value={globalsStore}>
      <Header />
      <Drawer />
      <Routes />
    </GlobalsContext.Provider>
  );
});

export default App;